/**
 * Card component for evaluating a single name with draggable candidates
 */

import React, { useEffect, useCallback } from 'react'
import { Card, Separator, Row, colors, DataBlock, BillboardText, Paragraph, useContainerWidth } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useDraggableCollection } from '@fs/zion-frontend-friends'
import { useDragAndDrop } from '../../hooks/useDragAndDrop'
import { CandidateItem } from '../CandidateItem/CandidateItem'
import type { NameEntry, Candidate } from '../../types'

interface NameEvaluationCardProps {
  name: NameEntry
  onCandidatesReorder: (reorderedCandidates: Candidate[]) => void
  navigationFooter?: React.ReactNode
}

export const NameEvaluationCard: React.FC<NameEvaluationCardProps> = ({
  name,
  onCandidatesReorder,
  navigationFooter,
}) => {
  // Use Zion's draggable collection hook
  const { draggedItems, dragApi, reset } = useDraggableCollection(name.candidates)
  const nativeDragApi = useDragAndDrop(dragApi)
  const [t] = useTranslation()
  const atWidth = useContainerWidth()
  const isMobile = atWidth({ default: true, md: false })

  // Touch drag state
  const [touchDraggedIndex, setTouchDraggedIndex] = React.useState<number | null>(null)

  // Reset when name or candidates change
  useEffect(() => {
    reset(name.candidates)
  }, [name.id, name.candidates, reset])

  // Update parent's candidate list when drag completes
  const handleDrop = (): void => {
    if (draggedItems.length > 0) {
      onCandidatesReorder(draggedItems)
    }
  }

  // Touch drag handlers
  const handleTouchDragStart = useCallback((dragIndex: number) => {
    setTouchDraggedIndex(dragIndex)
  }, [])

  const handleTouchDragOver = useCallback(
    (clientY: number) => {
      if (touchDraggedIndex === null) return

      // Find the element at the touch position
      const elements = document.querySelectorAll('[data-drag-index]')
      let targetIndex = -1

      elements.forEach((el) => {
        const rect = el.getBoundingClientRect()
        const elIndex = Number.parseInt((el as HTMLElement).dataset.dragIndex || '-1', 10)

        // If touch is over this element, use it as the target
        if (clientY >= rect.top && clientY <= rect.bottom) {
          targetIndex = elIndex
        }
      })

      // Perform the swap if we found a target and it's different from current position
      if (targetIndex !== -1 && targetIndex !== touchDraggedIndex) {
        const newOrder = [...draggedItems]
        const [movedItem] = newOrder.splice(touchDraggedIndex, 1)
        newOrder.splice(targetIndex, 0, movedItem)
        reset(newOrder)
        setTouchDraggedIndex(targetIndex)
        onCandidatesReorder(newOrder)
      }
    },
    [touchDraggedIndex, draggedItems, reset, onCandidatesReorder]
  )

  const handleTouchDragEnd = useCallback(() => {
    setTouchDraggedIndex(null)
  }, [])

  // Keyboard shortcuts for quick ranking
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const key = e.key
      const activeElement = document.activeElement as HTMLElement
      const currentIndexStr = activeElement?.dataset?.dragIndex

      if (!currentIndexStr) return

      const focusedIndex = Number.parseInt(currentIndexStr, 10)
      if (focusedIndex < 0 || focusedIndex >= draggedItems.length) return

      let targetIndex: number | null = null

      // Arrow keys: move up/down by 1
      if (key === 'ArrowUp' && focusedIndex > 0) {
        targetIndex = focusedIndex - 1
        e.preventDefault()
      } else if (key === 'ArrowDown' && focusedIndex < draggedItems.length - 1) {
        targetIndex = focusedIndex + 1
        e.preventDefault()
      }
      // Number keys 1-5: jump to specific position
      else if (/^[1-5]$/.test(key)) {
        targetIndex = Number.parseInt(key, 10) - 1
        if (targetIndex === focusedIndex) return // Already at target
        e.preventDefault()
      }

      // Perform the move
      if (targetIndex !== null && targetIndex >= 0 && targetIndex < draggedItems.length) {
        const newOrder = [...draggedItems]
        const [movedItem] = newOrder.splice(focusedIndex, 1)
        newOrder.splice(targetIndex, 0, movedItem)
        reset(newOrder) // Update internal state
        onCandidatesReorder(newOrder)

        // Refocus the moved item after React updates
        setTimeout(() => {
          const movedElement = document.querySelector(`[data-drag-index="${targetIndex}"]`) as HTMLElement
          movedElement?.focus()
        }, 0)
      }
    },
    [draggedItems, onCandidatesReorder, reset]
  )

  const enhancedDragApi = {
    ...nativeDragApi,
    onDrop: (e: React.DragEvent<HTMLDivElement>) => {
      nativeDragApi.onDrop(e)
      handleDrop()
    },
  }

  return (
    <Row alignX="center">
      {/* eslint-disable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        onKeyDown={handleKeyDown}
        role="group"
        aria-label={t('transliteration-eval-page.name-evaluation-card.cand-eval.aria-label', 'Candidate evaluation')}
        tabIndex={0}
      >
        <Card margins="md">
          {/* Name and IPA display */}
          <Row gutters="xxs">
            <DataBlock
              noShrinkX
              inline
              heading={t('transliteration-eval-page.name-evaluation-card.english.label', 'English:')}
              data={name.original}
            />
            <DataBlock
              noShrinkX
              inline
              heading={t('transliteration-eval-page.name-evaluation-card.ipa.label', 'IPA:')}
              data={name.ipa}
            />
          </Row>
          <Separator size="sm" />

          <Row wrap>
            <BillboardText primaryColor={colors.green60}>
              <Paragraph size="xs">
                {t('transliteration-eval-page.name-evaluation-card.best-match.message', 'Best match at top')}
              </Paragraph>
            </BillboardText>
            <Paragraph size="xs">
              {isMobile
                ? t('transliteration-eval-page.name-evaluation-card.drag-rank-mobile.message', 'Drag to rank')
                : t(
                    'transliteration-eval-page.name-evaluation-card.drag-rank-desktop.message',
                    'Drag, use arrow keys, or press 1-5 to rank'
                  )}
            </Paragraph>
          </Row>

          <Separator size="sm" />

          {/* Draggable candidates list */}
          <ul
            style={{ listStyle: 'none', padding: 0, margin: 0 }}
            aria-label={t(
              'transliteration-eval-page.name-evaluation-card.candidates.aria-label',
              'Transliteration candidates'
            )}
          >
            {draggedItems.map((candidate, index) => (
              <li key={candidate.id} style={{ listStyle: 'none' }}>
                <CandidateItem
                  candidate={candidate}
                  dragIndex={index}
                  isFirst={index === 0}
                  nativeDragApi={enhancedDragApi}
                  onTouchDragStart={handleTouchDragStart}
                  onTouchDragOver={handleTouchDragOver}
                  onTouchDragEnd={handleTouchDragEnd}
                />
              </li>
            ))}
          </ul>

          {/* Navigation footer inside card */}
          {navigationFooter && (
            <>
              <Separator size="sm" />
              <Row alignX="center">{navigationFooter}</Row>
            </>
          )}
        </Card>
      </div>
      {/* eslint-enable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-noninteractive-element-interactions */}
    </Row>
  )
}
